/**
 * F-029 — Daily challenge (seeded deterministic RNG).
 *
 * dateSeed("2026-04-25") gives a stable 32-bit number; everyone playing
 * the same UTC date sees the same planet sequence so leaderboards
 * compare apples-to-apples.
 *
 * mulberry32 is the chosen PRNG: tiny, well-distributed, no library
 * dependency. seededTierGenerator is drop-in compatible with the queue's
 * generator signature.
 */

import type { TierGenerator } from "./planet-queue.ts";

/**
 * 64-bit string hash → 32-bit seed (FNV-1a). Stable across browsers.
 */
function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function dateSeed(isoDate: string): number {
  return fnv1a(`cosmic-suika:${isoDate}`);
}

export function todayUtcIso(now: Date = new Date()): string {
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

const DEFAULT_TIER_WEIGHTS: ReadonlyArray<number> = [50, 30, 15, 5];

export interface SeededGeneratorOptions {
  weights?: ReadonlyArray<number>;
}

export function seededTierGenerator(
  seed: number,
  options: SeededGeneratorOptions = {},
): TierGenerator {
  const weights = options.weights ?? DEFAULT_TIER_WEIGHTS;
  const total = weights.reduce((s, n) => s + n, 0);
  const rng = mulberry32(seed);
  return () => {
    let pick = rng() * total;
    for (let i = 0; i < weights.length; i += 1) {
      pick -= weights[i]!;
      if (pick <= 0) return i;
    }
    return weights.length - 1;
  };
}

export const __testing = { fnv1a, DEFAULT_TIER_WEIGHTS };
